import { Box, Typography } from "@mui/material";
import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { renderToString } from "react-dom/server";
import FacebookCircularProgress from "../../shared/shared/Loading";
import { getUrl } from "src/Utils/Common";
import { DropDownDashboard } from "../../../Pages/Dashboard/DropDownDashboard";
import { updateSalesType } from "src/Redux/twoColumnChartSlice/twoColumnChartSlice";
import ValueGrowthIndicator from "src/Pages/StatisticsTest/components/ValueGrowthIndicator";
import useRefreshData from "src/Utils/CustomHooks/useRefreshData";
import { theme } from "../../../Theme";

function TwoColumnChart({
  salesTypeManager: propSalesTypeManager,
  setSalesTypeManager: propSetSalesTypeManager,
  menuConfig: propMenuConfig,
  salesTypeRef,
}) {
  const reduxSalesTypeManager = useSelector(
    (state) => state.twoColumnChart.currentSalesType
  );
  const dispatch = useDispatch();

  const salesTypeManager =
    propSalesTypeManager !== undefined
      ? propSalesTypeManager
      : reduxSalesTypeManager;
  const setSalesTypeManager =
    propSetSalesTypeManager !== undefined
      ? propSetSalesTypeManager
      : (val) => dispatch(updateSalesType(val));

  const currentDateComparison = useSelector(
    (state) => state.dateCompare.currentDateComparison
  );
  const [minValue, setMinValue] = useState();
  const [maxValue, setMaxValue] = useState();
  const currentChartType = useSelector((state) => state.chart.currentChartType);
  // const currentChartTypePerformance = useSelector(
  //   (state) => state.brandPerformanceChart.currentChartTypePerformance
  // );

  // State for the title and data
  const [title, setTitle] = useState("Value Sales Growth vs. YO");
  const [dataValues, setDataValues] = useState([]);
  const [selectedLabel, setSelectedLabel] = useState("");
  const [categories, setCategories] = useState([
    "Category Growth",
    "Brand3_growth",
  ]);
  const [menuConfig, setMenuConfig] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);


  // Get project_id from Redux and sales_type
  const projectId = useSelector((state) => state.userProject.projectId);
  const [salesType, setSalesType] = useState("value_sales"); // Default sales type
  const selectedFilter = useSelector((state) => state.filter.selectedFilter);

  const resetChartData = () => {
    setDataValues([]);
    setMinValue(undefined);
    setMaxValue(undefined);
    setSelectedLabel("");
  };

  const resetChartFilters = () => {
    setSalesType("value_sales");
    if (setSalesTypeManager) {
      const brandKey = Object.keys(menuConfig).find(
        (k) => k.toLowerCase() === "brand"
      );
      const firstBrand = brandKey ? menuConfig[brandKey]?.[0] : null;
      setSalesTypeManager(firstBrand ? `${brandKey},${firstBrand}` : null);
    }
  };

  const resetChartError = () => {
    setError(null);
  };

  const resetChartLoading = () => {
    setLoading(false);
  };

  const { handleRefresh, isRefreshing } = useRefreshData(
    () => {
      setLoading(true);
    },
    [resetChartData, resetChartFilters, resetChartError, resetChartLoading]
  );

  useEffect(() => {
    function fetchHierarchyData() {
      if (!projectId) {
        console.warn("Project ID is not set in Redux state.");
        setError("Project ID not available");
        return;
      }

      fetch(`${getUrl()}/market_overview/hierarchy`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "x-access-token": localStorage.getItem("userId") || "",
            "x-cors-api-key": "temp_8c8f91f122df0ef9daa49a6701f13f76",
          },
          body: JSON.stringify({ project_id: projectId }),
        })
        .then((response) => {
          if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
          }
          return response.json();
        })
        .then((result) => {
          const hierarchyData =
            result?.["0"] ||
            result?.Product ||
            result?.product ||
            {};
          if (Object.keys(hierarchyData).length === 0) {
            throw new Error("No hierarchy data available");
          }

          // Format menuConfig dynamically from API response
          // const dynamicMenuConfig = { "All Market": Object.keys(hierarchyData) }; // Add 'All Market'
          const dynamicMenuConfig = {}; // Add 'All Market'
          Object.entries(hierarchyData).forEach(([key, values]) => {
            if (
              key !== "price_bracket" &&
              key !== "size" &&
              key !== "size_bracket" &&
              key !== "sku_db" && key !== "sku_id"
            ) {
              dynamicMenuConfig[key] = values;
            }
          });
  
          dynamicMenuConfig["channel"] = [];
          dynamicMenuConfig["retailer"] = [];

          setMenuConfig(dynamicMenuConfig); // Update state with API response

          // Auto-select the first brand if no selection has been made yet 
          if (!salesTypeManager) {
            const brandKey = Object.keys(dynamicMenuConfig).find(
              (k) => k.toLowerCase() === "brand"
            );
            const firstBrand = brandKey ? dynamicMenuConfig[brandKey]?.[0] : null;
            if (firstBrand) {
              setSalesTypeManager(`${brandKey},${firstBrand}`);
            }
          }

          setError(null);
      }) 
      .catch ((error) => {
        console.error("Error fetching hierarchy data:", error);
        setError(error.message || "Failed to fetch hierarchy data");
      });
    }

    fetchHierarchyData();
  }, [projectId]);

  // ✅ Construct API filters dynamically based on selectedFilter
  const getApiFilters = () => {
    if (!selectedFilter || selectedFilter.length === 0) return {};

    return selectedFilter.reduce((acc, item) => {
      const { category, name } = item;

      if (!acc[category]) {
        acc[category] = []; // Initialize category array if not exists
      }

      acc[category].push(name); // Push the name value
      return acc;
    }, {});
  };

  useEffect(() => {
    function fetchMarketBrandSales() {
      if (!projectId) {
        console.warn("Project ID is not set in Redux state.");
        setError("Project ID not available");
        setLoading(false);
        return;
      }

      let [hierarchy, hierarchy_value] = currentChartType?.split(",") || [
        null,
        null,
      ];

      if (salesTypeManager) {
        [hierarchy, hierarchy_value] = salesTypeManager.split(",");
      } else {
        // When no selection yet (e.g. after a reset), use first brand from menuConfig
        const brandKey = Object.keys(menuConfig).find(
          (k) => k.toLowerCase() === "brand"
        );
        const firstBrand = brandKey ? menuConfig[brandKey]?.[0] : null;

        if (firstBrand) {
          hierarchy = brandKey;
          hierarchy_value = firstBrand;

          // Keep DropDownDashboard label in sync with the actual filter
          if (setSalesTypeManager) {
            setSalesTypeManager(`${brandKey},${firstBrand}`);
          }
        } else {
          return;
        }
      }

      setLoading(true);
      setError(null);

      fetch(`${getUrl()}/market_overview/market_brand_sales`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "x-access-token": localStorage.getItem("userId") || "",
              "x-cors-api-key": "temp_8c8f91f122df0ef9daa49a6701f13f76",
            },
            body: JSON.stringify({
              project_id: projectId,
              hierarchy: hierarchy.toString(),
              hierarchy_value: hierarchy_value.toString(),
              period_level: "years",
              // market: "Market1",
              area: null,
              target: 3,
              sales_type: salesType,
              filters: getApiFilters(),
            }),
          }
        )
        .then((response) => {
          if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
          }
          return response.json();
        })
        .then((result) => {
          // Update chart title based on sales_type
          const updatedTitle =
            salesType === "value_sales"
              ? "Value Sales Growth vs. YO"
              : "Volume Sales Growth vs. YO";

          // Update the chart data
          setTitle(updatedTitle);

          // Extract numerical values dynamically (ignoring "index" key)
          const allValues = Object.keys(result)
            .filter((key) => key !== "index") // Exclude "index"
            .flatMap((key) => result[key]) // Flatten the arrays
            .filter((value) => typeof value === "number"); // Ensure only numbers

          const fetchedMin = Math.min(...allValues);
          const fetchedMax = Math.max(...allValues);
          setMinValue(fetchedMin);
          setMaxValue(fetchedMax);

          // Find the selected filter value of the same hierarchy
          const selectedLabel =
            selectedFilter?.find((item) => item.category === hierarchy)?.name ||
            hierarchy_value; // Fallback to hierarchy_value if not found
            setSelectedLabel(selectedLabel);

          const formattedData = Object.keys(result).filter((key) => key !== "index") // Exclude "index"
            const transformedData = [];
          
            if (formattedData.length >= 2) {
              let marketGrowthValue = 0;
              let brandGrowthValue = 0;

              const marketKey = formattedData.find(key => key.toLowerCase().includes('market')) || formattedData[1];
              const brandKey = formattedData.find(key => key.toLowerCase().includes('brand')) || formattedData[0];

              marketGrowthValue = result[marketKey]?.[0] || 0;
              brandGrowthValue = result[brandKey]?.[0] || 0;

              transformedData.push({
                name: "Growth Comparison",
                data: [marketGrowthValue, brandGrowthValue] 
              });

            } else if (formattedData.length === 1) {
              
              const singleValue = result[formattedData[0]][0] || 0;
              transformedData.push({
                name: "Growth Comparison",
                data: [singleValue, 0] 
              });
            }
            
            if (!transformedData || transformedData.length === 0) {
              throw new Error("No data available for selected filters");
            }
            
            setDataValues(transformedData);
            console.log("transformedData", transformedData);
            setError(null);
      }) 
      .catch ((error) => {
        console.error("Error fetching market brand sales data:", error);
        setError(error.message || "Failed to fetch market brand sales data");
      })
      .finally(() => {
        setLoading(false); 
      });
    }

    fetchMarketBrandSales();
  }, [
    salesTypeManager,
    projectId,
    salesType,
    currentChartType,
    selectedFilter,
    menuConfig,
  ]); // Re-fetch when projectId, salesType, or menuConfig changes

  return (
    <>
      <Typography 
        sx={{
          fontWeight: "bold",
          fontSize: {
            xs: "0.9rem",        
            sm: "1rem",          
            md: "1.1rem",        
          },
          px: {
            xs: 0.5,             
            sm: 0.75,            
            md: 1,               
          },
          color: theme.palette.primary.main,
          textAlign: "left",
        }}
      >
        {title}
      </Typography>

      <DropDownDashboard
        menuConfig={menuConfig}
        local={true}
        name="TwoColumnChart"
        salesTypeManager={salesTypeManager}
        setSalesTypeManager={setSalesTypeManager}
        salesTypeRef={salesTypeRef}
        variant="local"
      />

      {loading || isRefreshing ? (
        <FacebookCircularProgress />
      ) : error ? (
        <Box
          sx={{
            width: "100%",
            minHeight: 200,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            margin: "0 auto"
          }}
        >
          <FacebookCircularProgress 
            loading={false}
            hasData={false}
            onRefresh={handleRefresh}
            message={`${error} - Click to reset chart`}
          />
        </Box>
      ) : !dataValues || dataValues.length === 0 ? (
        <Box
          sx={{
            width: "100%",
            minHeight: 200,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            margin: "0 auto"
          }}
        >
          <FacebookCircularProgress 
            loading={false}
            hasData={false}
            onRefresh={handleRefresh}
            message="No data available - Click to reset chart"
          />
        </Box>
      ) : (
        <ValueGrowthIndicator
          dataValues={dataValues}
          selectedLabel={selectedLabel}
          displayMode="market"
        />
      )}
    </>
  );
}

export default TwoColumnChart;

  // const [options] = useState({
  //   chart: {
  //     type: "bar",
  //     toolbar: {
  //       show: false,
  //     },
  //   },
  //   colors: ["#08306B", "#CD393B"], // Ensuring the colors match legend
  //   dataLabels: {
  //     enabled: true,
  //     formatter: function (val) {
  //       return `${val.toFixed(2)}%`; // Format labels as percentages
  //     },
  //     offsetY: -8, // Moves the label above the bars
  //     style: {
  //       fontSize: "12px",
  //       fontWeight: "bold",
  //       colors: ["#E0E0E0"],
  //     },
  //   },
  //   plotOptions: {
  //     bar: {
  //       columnWidth: "40%", // Increased to create more space between bars
  //       barHeight: "100%",
  //       endingShape: "rounded",
  //       borderRadius: 6,
  //       distributed: false, // Keep same category bars together
  //       colors: {
  //         backgroundBarColors: ["#E0E0E0"], // Light grey background for unselected bars
  //         backgroundBarOpacity: 0.2,
  //       },
  //     },
  //   },
  //   grid: {
  //     show: false,
  //   },
  //   responsive: [
  //     {
  //       breakpoint: 480,
  //       options: {
  //         legend: {
  //           position: "bottom",
  //           offsetX: -10,
  //           offsetY: 0,
  //         },
  //       },
  //     },
  //   ],
  //   xaxis: {
  //     categories: categories,
  //     labels: {
  //       show: true, // Show category labels under the bars
  //       fontSize: "10px",
  //       fontWeight: 600,
  //     },
  //     tooltip: {
  //       enabled: true,
  //     },
  //     axisBorder: {
  //       show: false,
  //     },
  //   },
  //   yaxis: {
  //     labels: {
  //       show: false,
  //     },
  //     min: minValue,
  //     max: maxValue,
  //     title: {
  //       text: "Growth (%)",
  //       offsetX: 20,
  //       style: {
  //         fontSize: "12px",
  //         fontWeight: "bold",
  //       },
  //     },
  //   },
  //   fill: {
  //     opacity: 1,
  //   },
  //   tooltip: {
  //     enabled: true,
  //     y: {
  //       formatter: (val) => `${val.toFixed(2)}%`,
  //     },
  //     theme: "dark",
  //   },
  //   legend: {
  //     position: "bottom",
  //     offsetX: 5,
  //     offsetY: 5,
  //     markers: {
  //       radius: 5, // Rounded markers
  //     },
  //     fontSize: "12px",
  //     fontWeight: 600,
  //   },
  // });

  // function customFormatter(seriesName, opts) {
  //   const dataPointIndex = opts.seriesIndex; // Index of the current data point (category)
  //   const value = opts.w.globals.series[0][dataPointIndex]; // Retrieve the value for the current data point

  //   const jsxString = renderToString(
  //     <Box marginX={1}>
  //       <Typography sx={{ fontWeight: "800" }}>{seriesName}</Typography>
  //       <Typography>{value} M</Typography>
  //     </Box>
  //   );

  //   return `${seriesName}: ${value}`;
  // }
